import type { AutomaticPolicy } from './proactiveAutomation.ts';
import { connectorRequestHash } from './connectorWriteSafety.ts';
export const PROACTIVE_SOURCES = ['agenda', 'tasks', 'files', 'email'] as const;
export type ProactiveSource = typeof PROACTIVE_SOURCES[number];
export interface ProactivePreferences {
  enabled: boolean;
  sources: ProactiveSource[];
  automatic: boolean;
  background?: boolean;
  automatic_policy?: AutomaticPolicy;
  revision: number;
  last_requested_at: string | null;
}
export interface Suggestion {
  id: string;
  kind: 'upcoming' | 'conflict' | 'overdue' | 'due' | 'unread' | 'file';
  source: ProactiveSource;
  provider: string;
  reference: string;
  title: string;
  detail?: string;
  at?: string;
  expires_at: string;
  related?: string;
}
export interface SourceState {
  source: ProactiveSource;
  provider: string;
  status: 'ready' | 'error' | 'quota' | 'disconnected' | 'disabled';
  sampled?: boolean;
}
export interface ProactiveSnapshot {
  origin?: 'foreground' | 'background';
  items: Suggestion[];
  sources: SourceState[];
  generated_at: string;
}
export interface ProactiveFeedback {
  suggestion_id: string;
  action: 'ignored' | 'snoozed' | 'opened';
  hidden_until: string;
  expires_at: string;
  updated_at: string;
}
// One retained interaction per suggestion for seven days, not an immutable audit log.
export function feedbackHistory(rows: ProactiveFeedback[], now: number): ProactiveFeedback[] {
  return rows.filter((row) =>
    /^[0-9a-f]{64}$/.test(row.suggestion_id) &&
    ['ignored', 'snoozed', 'opened'].includes(row.action) &&
    Number.isFinite(Date.parse(row.hidden_until)) &&
    Number.isFinite(Date.parse(row.updated_at)) && Date.parse(row.expires_at) > now
  ).sort((a, b) =>
    Date.parse(b.updated_at) - Date.parse(a.updated_at) || a.suggestion_id.localeCompare(b.suggestion_id)
  ).slice(0, 50).map((row) => ({
    suggestion_id: row.suggestion_id,
    action: row.action,
    hidden_until: row.hidden_until,
    expires_at: row.expires_at,
    updated_at: row.updated_at,
  }));
}
export const DEFAULT_PROACTIVE: ProactivePreferences = {
  enabled: false,
  sources: ['agenda', 'tasks', 'files'],
  automatic: false,
  revision: 0,
  last_requested_at: null,
};
export class ProactiveError extends Error {
  constructor(code: string, public status = 400) {
    super(code);
  }
}
export const cleanText = (value: unknown, max = 240) =>
  typeof value === 'string' ? value.replace(/[\u0000-\u001f\u007f]/g, ' ').trim().slice(0, max) : '';
export function validatePreferences(value: Record<string, unknown>) {
  if (
    typeof value.enabled !== 'boolean' || typeof value.automatic !== 'boolean' ||
    !Array.isArray(value.sources) ||
    value.sources.length > 4 ||
    value.sources.some((s) => !PROACTIVE_SOURCES.includes(s as ProactiveSource)) ||
    !Number.isInteger(value.revision) || Number(value.revision) < 0
  ) throw new ProactiveError('invalid_preferences');
  return {
    enabled: value.enabled,
    automatic: value.enabled && value.automatic,
    sources: [...new Set(value.sources)] as ProactiveSource[],
    revision: Number(value.revision),
  };
}
export async function suggestion(input: Omit<Suggestion, 'id'>): Promise<Suggestion> {
  return {
    ...input,
    id: await connectorRequestHash({
      source: input.source,
      provider: input.provider,
      reference: input.reference,
      kind: input.kind,
      at: input.at ?? '',
      related: input.related ?? '',
    }),
  };
}
export interface AgendaSignal {
  id: string;
  title: string;
  start_at: string;
  end_at: string;
  deleted_at?: string | null;
  all_day_start?: string | null;
}
export interface TaskSignal {
  id: string;
  title: string;
  due_date: string;
  done: boolean;
  source?: string;
}
export interface FileSignal {
  id: string;
  name: string;
  provider: string;
  external_id: string;
  expires_at: string;
}
export async function localSuggestions(
  agenda: AgendaSignal[],
  tasks: TaskSignal[],
  files: FileSignal[],
  now: number,
) {
  const items: Suggestion[] = [],
    horizon = now + 48 * 3600_000,
    ttl = new Date(now + 24 * 3600_000).toISOString();
  const events = agenda.filter((e) =>
    !e.deleted_at && Date.parse(e.end_at) > now && Date.parse(e.start_at) < horizon
  ).sort((a, b) => Date.parse(a.start_at) - Date.parse(b.start_at));
  for (const e of events.slice(0, 16)) {
    if (!e.id) continue;
    const overlaps = !e.all_day_start &&
      events.find((x) =>
        x.id !== e.id && !x.all_day_start && Date.parse(x.start_at) < Date.parse(e.end_at) &&
        Date.parse(x.end_at) > Date.parse(e.start_at)
      );
    items.push(
      await suggestion({
        source: 'agenda',
        kind: overlaps ? 'conflict' : 'upcoming',
        provider: 'agenda',
        reference: e.id,
        title: cleanText(e.title),
        ...(overlaps ? { detail: cleanText(overlaps.title), related: overlaps.id } : {}),
        at: e.start_at,
        expires_at: new Date(Math.min(Date.parse(e.end_at), now + 24 * 3600_000)).toISOString(),
      }),
    );
  }
  for (
    const t of tasks.filter((t) =>
      !t.done && Number.isFinite(Date.parse(t.due_date)) && Date.parse(t.due_date) < horizon
    ).sort((a, b) => Date.parse(a.due_date) - Date.parse(b.due_date)).slice(0, 16)
  ) {
    if (!t.id) continue;
    items.push(
      await suggestion({
        source: 'tasks',
        kind: Date.parse(t.due_date) < now ? 'overdue' : 'due',
        provider: 'tasks',
        reference: t.id,
        title: cleanText(t.title),
        at: t.due_date,
        expires_at: ttl,
      }),
    );
  }
  for (const f of files.filter((f) => Date.parse(f.expires_at) > now).slice(0, 8)) {
    if (!['google_drive', 'onedrive', 'notion'].includes(f.provider) || !f.external_id) continue;
    items.push(
      await suggestion({
        source: 'files',
        kind: 'file',
        provider: f.provider,
        reference: f.external_id,
        title: cleanText(f.name),
        expires_at: new Date(Math.min(Date.parse(f.expires_at), now + 24 * 3600_000)).toISOString(),
      }),
    );
  }
  return items;
}
export async function emailSuggestions(provider: string, messages: Record<string, unknown>[], now: number) {
  const result: Suggestion[] = [];
  for (const m of messages.slice(0, 5)) {
    const unread = provider === 'gmail'
      ? Array.isArray(m.label_ids) && m.label_ids.includes('UNREAD')
      : m.is_read === false;
    const date = Date.parse(String(m.date));
    if (!unread || !m.id || !Number.isFinite(date) || date < now - 7 * 86400_000 || date > now + 300_000) {
      continue;
    }
    result.push(
      await suggestion({
        source: 'email',
        kind: 'unread',
        provider,
        reference: cleanText(m.id, 500),
        title: cleanText(m.subject),
        detail: cleanText(m.from),
        at: new Date(date).toISOString(),
        expires_at: new Date(now + 24 * 3600_000).toISOString(),
      }),
    );
  }
  return result;
}
export function visibleSuggestions(
  snapshot: ProactiveSnapshot | null,
  feedback: { suggestion_id: string; hidden_until: string }[],
  now: number,
) {
  const hidden = new Set(
    feedback.filter((f) => Date.parse(f.hidden_until) > now).map((f) => f.suggestion_id),
  );
  return (snapshot?.items ?? []).filter((i) => Date.parse(i.expires_at) > now && !hidden.has(i.id)).sort(
    (a, b) => {
      const rank = { conflict: 0, overdue: 1, upcoming: 2, due: 3, unread: 4, file: 5 };
      return rank[a.kind] - rank[b.kind] || (a.at ?? '').localeCompare(b.at ?? '') ||
        a.id.localeCompare(b.id);
    },
  ).slice(0, 30);
}
